import { eventsApiBaseUrl } from "../config/eventsApi";
import {
  DEFAULT_WEBSITE_LEADERSHIP,
  LEADERSHIP_GROUPS,
} from "../data/leadershipContent";

const MEMBERS = DEFAULT_WEBSITE_LEADERSHIP.groups.flatMap((group) => group.members);
const LEGACY_PHOTOS = new Set(MEMBERS.map((member) => member.photo).filter(Boolean));
const LEGACY_WEBP = new Map(
  MEMBERS.filter((member) => member.photoWebp).map((member) => [
    member.photo,
    member.photoWebp,
  ]),
);
const OWNED_IMAGE =
  /^\/api\/public\/site-content\/leadership\/images\/[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const MEMBER_ID = /^[a-z0-9][a-z0-9-]{0,79}$/;
const PHOTO_POSITION = /^(top|center|center (?:100|[1-9]?[0-9])%)$/;

export function leadershipImageSrc(path) {
  if (LEGACY_PHOTOS.has(path) || [...LEGACY_WEBP.values()].includes(path)) return path;
  if (typeof path !== "string" || !OWNED_IMAGE.test(path)) return null;
  return `${eventsApiBaseUrl()}${path}`;
}

function text(value, max, required = false) {
  if (typeof value !== "string" || value.trim().length > max) return null;
  const trimmed = value.trim();
  return required && !trimmed ? null : trimmed;
}

/** Reject malformed payloads as a whole; valid empty groups are intentional. */
export function publicLeadership(value) {
  if (!value || typeof value !== "object") return null;
  const title = text(value.title, 120, true);
  const subtitle = text(value.subtitle, 500);
  if (
    title === null ||
    subtitle === null ||
    (value.updatedAt !== null &&
      (typeof value.updatedAt !== "string" ||
        !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value.updatedAt) ||
        !Number.isFinite(Date.parse(value.updatedAt)))) ||
    !Array.isArray(value.groups) ||
    value.groups.length !== LEADERSHIP_GROUPS.length
  )
    return null;
  const ids = new Set();
  const groups = [];
  for (const [index, group] of value.groups.entries()) {
    if (
      !group ||
      group.id !== LEADERSHIP_GROUPS[index].id ||
      !Array.isArray(group.members) ||
      group.members.length > 50
    )
      return null;
    const members = [];
    for (const member of group.members) {
      if (
        !member ||
        typeof member.id !== "string" ||
        !MEMBER_ID.test(member.id) ||
        ids.has(member.id)
      )
        return null;
      const name = text(member.name, 120, true);
      const position = text(member.position, 120);
      if (
        name === null ||
        position === null ||
        typeof member.photoPosition !== "string" ||
        !PHOTO_POSITION.test(member.photoPosition)
      )
        return null;
      if (
        member.photo !== null &&
        (typeof member.photo !== "string" ||
          !(LEGACY_PHOTOS.has(member.photo) || OWNED_IMAGE.test(member.photo)))
      )
        return null;
      if (
        member.photoWebp !== null &&
        (typeof member.photoWebp !== "string" ||
          !member.photo ||
          member.photoWebp !== LEGACY_WEBP.get(member.photo))
      )
        return null;
      ids.add(member.id);
      if (ids.size > 100) return null;
      members.push({
        id: member.id,
        name,
        position,
        photo: member.photo,
        photoWebp: member.photoWebp,
        photoPosition: member.photoPosition,
      });
    }
    groups.push({ id: group.id, members });
  }
  return { title, subtitle, groups, updatedAt: value.updatedAt };
}

export async function fetchPublicLeadership({ signal } = {}) {
  const base = eventsApiBaseUrl();
  if (!base || signal?.aborted) return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  const onAbort = () => controller.abort();
  signal?.addEventListener("abort", onAbort);
  try {
    const response = await fetch(`${base}/api/public/site-content/leadership`, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return null;
    const data = JSON.parse(await response.text());
    return data?.ok === true ? publicLeadership(data.leadership) : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}
